import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { PlayerSeatDto } from '@/services/table/tableState';
import { replacePathParams } from '@/common/utils/utils';
import { RootState } from '@/services/app/reducer';
import { tableConnectAction } from '@/services/table/tableActions';
import TableStakeInfo from '@/components/TableStakeInfo/TableStakeInfo';
import sitout from '@/assets/sitout.svg';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import ROUTES from '@/routers/Routes';

import TableHeaderLabel from '../TableHeaderLabel/TableHeaderLabel';
import TableHeaderTimeCounter from '../TableHeaderTimeCounter/TableHeaderTimeCounter';
import CardFace, { CardSizes } from '@/components/CardFace/CardFace';

interface TableTabParams {
  tableId: string;
  tableUrl: string;
  userId: string;
}

const TableTab: FC<TableTabParams> = ({ tableId, userId, tableUrl }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(tableConnectAction(tableId, tableUrl));
  }, [dispatch, tableId, tableUrl, userId]);

  const tableState = useSelector((state: RootState) => state.tables[tableId]);

  const onClickTabHandler = () => navigate(replacePathParams(ROUTES.TABLE, { tableId: tableState?.tableId }));

  if (!tableState) {
    return null;
  }

  if (!tableState?.playerSeat)
    return (
      <>
        <div className="mr-2 flex h-11 items-center justify-center gap-2.5 rounded-md border-2 border-white border-opacity-30 p-1.5 text-sm font-medium text-white text-opacity-50">
          <div className="flex flex-col">
            <TableHeaderLabel showOpacity>
              {`${tableState?.currentVariant?.bettingStructure} 
            ${tableState?.currentVariant?.category}`}
            </TableHeaderLabel>
            <TableHeaderLabel>
              <TableStakeInfo
                ante={tableState?.ante}
                smallBet={tableState?.smallBet}
                bigBet={tableState?.bigBet}
                smallBlind={tableState?.smallBlind}
                bigBlind={tableState?.bigBlind}
              />
            </TableHeaderLabel>
          </div>
        </div>
      </>
    );

  const renderSeat = (seat: PlayerSeatDto) => {
    if (tableState.dealerSeat === -1 || !tableState.handState) {
      return (
        <div className="flex flex-row px-1">
          <div className="flex flex-row items-center px-1">
            <LoadingSpinner size="3" />
            <span className="ml-2 text-xs font-bold">Starting</span>
          </div>
        </div>
      );
    } else if (seat.waiting) {
      return (
        <div className="flex flex-row items-center px-1">
          <LoadingSpinner size="3" />
          <span className="ml-2 text-xs font-bold">Waiting</span>
        </div>
      );
    } else if (seat.active) {
      return (
        <div className="flex flex-row items-center px-1">
          <span className="text-xs font-bold">Dealing</span>
        </div>
      );
    } else if (seat.active === false) {
      return (
        <div className="flex flex-row items-center px-1">
          <img src={sitout} alt="sitting out" className="px-1" />
          <span className="text-xs font-bold">Sit Out</span>
        </div>
      );
    } else {
      return;
    }
  };

  const seat = tableState?.seats?.find((seat) => seat?.seat === tableState.playerSeat?.seat);

  const holeCards = tableState.playerSeat?.holeCards;

  return (
    <>
      <div className="flex h-11 grow items-center justify-center gap-2.5 rounded-md border-2 border-ph-purple-extra-light p-1.5 font-medium text-white text-opacity-50">
        <button onClick={onClickTabHandler}>
          <div className="flex flex-row items-center">
            {seat && (
              <TableHeaderTimeCounter
                timer={seat.timer}
                timeRemaining={seat.timeRemaining}
                usingTimeBank={seat.usingTimeBank}
              />
            )}
            {tableState?.handState && holeCards?.length > 0 && tableState.playerSeat?.cardIndexes?.length > 0 ? (
              <div className="flex flex-row justify-start">
                {tableState.playerSeat.cardIndexes.map((cardIndex, index) => {
                  const card = holeCards[index];
                  if (!card) {
                    return null;
                  }
                  return (
                    <CardFace
                      rank={card.rank}
                      suit={card.suit}
                      size={CardSizes.xxs}
                      key={`${cardIndex}-card-${card?.rank}${card?.suit}`}
                    />
                  );
                })}
              </div>
            ) : (
              renderSeat(tableState.playerSeat)
            )}
          </div>
        </button>
      </div>
    </>
  );
};

export default TableTab;
